// v2.19.35 — Watcher offres livreur : polling 5s sur /my-pending-offers.
// Si offre reçue → modal plein écran sliding-up avec countdown 30s.
// Boutons Accepter/Refuser. Son d'alerte court via Web Audio API (pas de fichier externe).
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const POLL_MS = 5000;

// v3.7.1 — Sonnerie livreur : mélodie répétée en boucle + vibration continue
// tant que l'offre est à l'écran. Retourne une fonction stop().
function _startRing() {
  let ctx = null;
  let interval = null;
  const playChime = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!ctx || ctx.state === "closed") ctx = new Ctx();
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      // Mélodie ascendante type sonnerie : E5 → A5 → C#6 → E6 (x2 rapide)
      const notes = [[659.3, 0], [880, 0.14], [1108.7, 0.28], [1318.5, 0.42], [880, 0.62], [1318.5, 0.76]];
      notes.forEach(([f, t]) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "triangle"; osc.frequency.value = f;
        gain.gain.setValueAtTime(0.0001, ctx.currentTime + t);
        gain.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + 0.3);
        osc.connect(gain).connect(ctx.destination);
        osc.start(ctx.currentTime + t);
        osc.stop(ctx.currentTime + t + 0.35);
      });
    } catch (_) {}
  };
  const vibrate = () => {
    try { if (navigator.vibrate) navigator.vibrate([300, 120, 300, 120, 500]); } catch (_) {}
  };
  playChime(); vibrate();
  interval = setInterval(() => { playChime(); vibrate(); }, 1800);
  return () => {
    if (interval) clearInterval(interval);
    try { if (navigator.vibrate) navigator.vibrate(0); } catch (_) {}
    try { if (ctx && ctx.state !== "closed") ctx.close().catch(() => {}); } catch (_) {}
  };
}

export default function CourierOffersWatch() {
  const [offer, setOffer] = useState(null);
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const lastSeenRef = useRef(null);
  const pollRef = useRef(null);
  const tickRef = useRef(null);
  const ringStopRef = useRef(null);

  const fetchOffers = useCallback(async () => {
    try {
      const r = await fetch(`${API}/v3/delivery/my-pending-offers`, { credentials: "include" });
      if (!r.ok) return;
      const d = await r.json();
      if (!d.ok || d.count === 0) {
        if (offer && !d.offers.find((o) => o.order_ref === offer.order_ref)) setOffer(null);
        return;
      }
      const top = d.offers[0];
      if (top.order_ref !== lastSeenRef.current) {
        lastSeenRef.current = top.order_ref;
        setOffer(top);
        setRemaining(top.remaining_ms);
      } else {
        setRemaining(top.remaining_ms);
      }
    } catch (_) {}
  }, [offer]);

  useEffect(() => {
    fetchOffers();
    pollRef.current = setInterval(fetchOffers, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchOffers]);

  // v3.7.1 — Sonnerie + vibration en boucle tant que l'offre est visible.
  useEffect(() => {
    if (offer && !ringStopRef.current) {
      ringStopRef.current = _startRing();
    }
    if (!offer && ringStopRef.current) {
      ringStopRef.current();
      ringStopRef.current = null;
    }
    return () => {
      if (ringStopRef.current) { ringStopRef.current(); ringStopRef.current = null; }
    };
  }, [offer]);

  useEffect(() => {
    if (!offer) { if (tickRef.current) clearInterval(tickRef.current); return; }
    tickRef.current = setInterval(() => {
      setRemaining((r) => {
        const next = r - 1000;
        if (next <= 0) { setOffer(null); return 0; }
        return next;
      });
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [offer]);

  const accept = async () => {
    if (!offer || busy) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/v3/delivery/accept`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order_ref: offer.order_ref }),
      });
      const d = await r.json();
      if (r.ok && d.ok) {
        toast.success(`✅ Course acceptée ${offer.order_ref}`);
        if (d.wa_client_url) window.open(d.wa_client_url, "_blank");
        // v3.6.5 — Branche la course acceptée sur le GPS cockpit : départ
        // boutique (pickup) → client (drop), en auto-play immersif.
        try {
          const pk = offer.pickup, dr = offer.drop;
          if (pk && dr && Number.isFinite(Number(pk.lat)) && Number.isFinite(Number(dr.lat))) {
            const q = `from=${pk.lat},${pk.lng}&dest=${dr.lat},${dr.lng}`
              + `&code=${encodeURIComponent(dr.address_civ || offer.order_ref || "")}`
              + `&label=${encodeURIComponent(dr.label || dr.description || "Client")}&sim=1`;
            setTimeout(() => { window.location.href = `/livreur/navigate?${q}`; }, 900);
          }
        } catch (_) { /* noop */ }
        setOffer(null);
      } else {
        toast.error(d.error || "Erreur");
        setOffer(null);
      }
    } catch (_) { toast.error("Erreur réseau"); }
    finally { setBusy(false); }
  };

  const refuse = async () => {
    if (!offer || busy) return;
    setBusy(true);
    try {
      await fetch(`${API}/v3/delivery/refuse`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order_ref: offer.order_ref }),
      });
      setOffer(null);
    } catch (_) {}
    finally { setBusy(false); }
  };

  if (!offer) return null;

  const sec = Math.max(0, Math.ceil(remaining / 1000));
  return (
    <div data-testid="courier-offer-modal" style={overlayStyle}>
      <div style={modalStyle}>
        <div style={{ textAlign: "center", marginBottom: 2 }}>
          <span data-testid="courier-offer-ring" style={{ display: "inline-block", fontSize: 26, transformOrigin: "top center", animation: "aya-bell-shake 0.9s ease-in-out infinite" }}>🔔</span>
          <style>{`@keyframes aya-bell-shake { 0%,100%{transform:rotate(0)} 15%{transform:rotate(18deg)} 30%{transform:rotate(-16deg)} 45%{transform:rotate(12deg)} 60%{transform:rotate(-8deg)} 75%{transform:rotate(4deg)} }`}</style>
        </div>
        <div style={{ fontSize: 11, color: "#FBBF24", fontWeight: 700, letterSpacing: 1.2, textAlign: "center" }}>NOUVELLE COURSE</div>
        <div style={{ fontFamily: "monospace", textAlign: "center", color: "#93C5FD", fontSize: 13, marginTop: 4 }}>{offer.order_ref}</div>

        <div style={{ margin: "18px 0 12px", textAlign: "center" }}>
          <div style={{ fontSize: 36, fontWeight: 800, color: "#22C55E" }}>{offer.courier_share_fcfa} FCFA</div>
          <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 2 }}>Ta part (85% du total {offer.delivery_fee_fcfa} FCFA)</div>
        </div>

        <div style={rowStyle}><span style={kStyle}>📦 Pickup</span><span style={vStyle}>{offer.commerce_name}</span></div>
        <div style={rowStyle}><span style={kStyle}>📍 Drop</span><span style={vStyle}>{offer.drop?.description || offer.drop?.address_civ || "—"}</span></div>
        <div style={rowStyle}><span style={kStyle}>📏 Distance</span><span style={vStyle}>{offer.distance_km} km</span></div>
        {offer.urgency === "express" && <div style={rowStyle}><span style={kStyle}>⚡ Urgence</span><span style={{ ...vStyle, color: "#FBBF24", fontWeight: 700 }}>EXPRESS</span></div>}

        <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
          <button onClick={refuse} disabled={busy} data-testid="courier-offer-refuse" style={refuseBtnStyle}>❌ Refuser</button>
          <button onClick={accept} disabled={busy} data-testid="courier-offer-accept" style={acceptBtnStyle}>
            ✅ Accepter ({sec}s)
          </button>
        </div>
        <div style={{ marginTop: 10, height: 4, background: "rgba(255,255,255,0.08)", borderRadius: 999, overflow: "hidden" }}>
          <div style={{ width: `${(remaining / 30000) * 100}%`, height: "100%", background: "#22C55E", transition: "width 1s linear" }} />
        </div>
      </div>
    </div>
  );
}

const overlayStyle = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)", display: "flex", alignItems: "flex-end", justifyContent: "center", zIndex: 9999, padding: 14, animation: "fadeIn 0.2s ease-out" };
const modalStyle = { background: "radial-gradient(ellipse at top, #14223D 0%, #0A0F1F 70%)", color: "#E8ECF7", borderRadius: 18, padding: 22, maxWidth: 460, width: "100%", border: "1.5px solid rgba(34,197,94,0.4)", boxShadow: "0 -10px 50px rgba(34,197,94,0.3), 0 10px 40px rgba(0,0,0,0.6)", marginBottom: 20, fontFamily: "system-ui, sans-serif", animation: "slideUp 0.3s ease-out" };
const rowStyle = { display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid rgba(255,255,255,0.06)", fontSize: 13 };
const kStyle = { color: "#94A3B8" };
const vStyle = { color: "#E8ECF7", fontWeight: 600, textAlign: "right", flex: 1, marginLeft: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const acceptBtnStyle = { flex: 2, background: "linear-gradient(135deg, #16A34A, #22C55E)", color: "#0F172A", border: "none", padding: "14px 16px", borderRadius: 12, fontWeight: 800, fontSize: 15, cursor: "pointer", boxShadow: "0 4px 14px rgba(34,197,94,0.4)" };
const refuseBtnStyle = { flex: 1, background: "rgba(248,113,113,0.12)", color: "#F87171", border: "1px solid rgba(248,113,113,0.35)", padding: "14px 16px", borderRadius: 12, fontWeight: 700, fontSize: 14, cursor: "pointer" };
